import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';

import CardSlider from 'components/consumer/CardSlider';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import { GET_RECENTLY_VIEWED } from 'mocks/queries';
import { getRecentlyViewed } from 'components/consumer/cart/utils';
import useStaticProductCardData from 'hooks/useStaticProductCardData';
import PDPSectionCarousel from 'components/consumer/PDP/PDPSectionCarousel';
import ProductCardStatic from '../ProductCardStatic';

const RecentlyViewed = ({
  isAdmin = false,
  currentPDPProduct = '',
  isLayoutRevamp = false,
}) => {
  const { data, error, loading } = useQuery(GET_RECENTLY_VIEWED);

  const productIds = useMemo(() => {
    // if there are no products on server then look in localstorage as
    // user might not be logged in
    const unfilteredIds = data?.getRecentlyViewed?.length
      ? data?.getRecentlyViewed?.map(id => parseInt(id, 10))
      : getRecentlyViewed().map(id => parseInt(id, 10));

    const filteredIds = unfilteredIds.filter(
      id => id !== parseInt(currentPDPProduct, 10)
    );
    return filteredIds;
  }, [currentPDPProduct, data?.getRecentlyViewed]);

  const { products: staticProductData } = useStaticProductCardData({
    productIds,
  });

  if (isAdmin) {
    return (
      <div className="flex items-center justify-center sm:m-10">
        <div className="p-5 rounded border border-dotted border-gray-light3">
          Recently viewed component placeholder for Variant builder. The
          component will display correctly in Consumer.
        </div>
      </div>
    );
  }

  if (loading || error || !staticProductData.length) {
    return null;
  }

  if (isLayoutRevamp) {
    return (
      <div data-xc="RecentlyViewedRevamp">
        <PDPSectionCarousel title="Recently viewed">
          {staticProductData.map(product => (
            <div className="h-full" key={`recently-viewed-${product?.id}`}>
              <ProductCardStatic layout="minimal" {...product} />
            </div>
          ))}
        </PDPSectionCarousel>
      </div>
    );
  }

  return (
    <div
      className={classNames('px-0 min-[1440px]:px-0 min-[1440px]:py-[4.5rem]', {
        'py-10': !currentPDPProduct,
        'py-[.1rem]': !!currentPDPProduct,
      })}
    >
      <FixedWidthContainer wide>
        <div className="text-2xl font-bold text-gray mb-6">Recently viewed</div>
      </FixedWidthContainer>

      <CardSlider>
        {staticProductData?.map(product => (
          <ProductCardStatic
            key={`recently-viewed-${product?.id}`}
            layout="minimal"
            {...product}
          />
        ))}
      </CardSlider>
    </div>
  );
};
RecentlyViewed.propTypes = {
  isAdmin: PropTypes.bool,
  currentPDPProduct: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLayoutRevamp: PropTypes.bool,
};

export default RecentlyViewed;
